import React, { useEffect, useState } from 'react';
import TenantSwitcher from '@/components/TenantSwitcher';
import OnboardingTour from '@/components/OnboardingTour';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, ClipboardList, Package, Wrench, Users as CrmIcon, Calendar, FileText, 
  BarChart3, Settings as SettingsIcon, Menu, X, Bell, Search, LogIn, Briefcase, 
  UserCog, LogOut, QrCode, PlusSquare, LifeBuoy, BookOpen, Building, Banknote, MessageSquare,
  Navigation
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { resolveSidebarLogo } from '@/lib/branding';

const Layout = ({ children, userLogo, setUserLogo }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  const location = useLocation();

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => {
      setIsLargeScreen(event.matches);
      if (event.matches) setSidebarOpen(false);
    };
    setIsLargeScreen(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  const navigate = useNavigate();
  const { user, profile, currentTenant, signOut } = useAuth();
  const { toast } = useToast();

  const customerNavigation = [
    { name: 'My Portal', href: '/portal', icon: Building, roles: ['customer'] },
  ];

  const baseNavigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['owner', 'supervisor', 'technician', 'system_admin', 'admin'] },
    { name: 'Platform Console', href: '/admin/dashboard', icon: UserCog, roles: ['system_admin', 'admin'] },
    { name: 'My Company', href: '/company', icon: Building, roles: ['owner', 'supervisor'] },
    { name: 'Invite Team', href: '/company/invite', icon: UserCog, roles: ['owner', 'supervisor'] },
    { name: 'Work Orders', href: '/work-orders', icon: ClipboardList, roles: ['owner', 'supervisor', 'technician'] },
    { name: 'Company Chat', href: '/chat', icon: MessageSquare, roles: ['owner', 'supervisor', 'technician'] },
    { name: 'Property Tasks', href: '/property-tasks', icon: Package, roles: ['owner', 'supervisor', 'technician'] },
    { name: 'Assets', href: '/assets', icon: Package, roles: ['owner', 'supervisor', 'technician'] },
    { name: 'Maintenance', href: '/maintenance', icon: Wrench, roles: ['owner', 'supervisor', 'technician'] },
    { name: 'Customers', href: '/crm', icon: CrmIcon, roles: ['owner', 'supervisor'] },
    { name: 'Scheduling', href: '/scheduling', icon: Calendar, roles: ['owner', 'supervisor', 'technician'] },
    { name: 'Dispatch Board', href: '/gps', icon: Navigation, roles: ['owner', 'supervisor', 'technician'] },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, roles: ['owner', 'supervisor'] },
  ];

  const financialNavigation = [
    { name: 'Financials', href: '/financials', icon: Banknote, roles: ['owner', 'supervisor'] },
  ];
  
  const adminNavigation = [
     { name: 'User Management', href: '/admin/users', icon: UserCog, roles: ['owner', 'supervisor'] },
  ];

  const helpNavigation = [
    { name: 'Financial App Guide', href: '/financial-app-guide', icon: BookOpen, roles: ['owner', 'supervisor', 'technician'] },
    { name: 'Multi-Tenancy Guide', href: '/multi-tenancy-guide', icon: LifeBuoy, roles: ['owner', 'supervisor', 'technician'] },
  ];

  const settingsNavigation = [
    { name: 'Settings', href: '/settings', icon: SettingsIcon, roles: ['owner', 'supervisor', 'technician'] },
  ];

  const getFilteredNavigation = (navItems) => {
    if (!profile) return [];
    return navItems.filter(item => item.roles.includes(profile.role));
  };

  const handleLogout = async () => {
    try {
      await signOut();
      toast({ title: 'Logged Out', description: 'You have been successfully logged out.', variant: 'success' });
      navigate('/login');
    } catch (error) {
      toast({ title: 'Logout Failed', description: error.message, variant: 'destructive' });
    }
  };


  const currentUserName = profile?.full_name || user?.email || 'Guest User';
  const currentUserRole = profile?.role || 'guest';
  const sidebarLogo = resolveSidebarLogo(currentTenant, profile);
  const userAvatar = profile?.avatar_url || userLogo || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUserName)}&background=random&color=fff`;

  return (
    <div className="flex h-screen">
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <motion.div
        initial={false}
        animate={{ x: isLargeScreen ? 0 : (sidebarOpen ? 0 : -300) }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className={`fixed inset-y-0 left-0 z-50 w-64 shrink-0 bg-slate-900/95 backdrop-blur-xl border-r border-slate-700 lg:relative lg:block ${
          sidebarOpen ? 'block' : 'hidden lg:block'
        }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center justify-between px-6 border-b border-slate-700">
            <Link to="/" className="flex items-center max-w-[200px]" title="Home">
              <img src={sidebarLogo} alt="Company logo" className="h-10 w-auto max-w-full object-contain" />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-slate-300 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto scrollbar-hide">
            {getFilteredNavigation(profile?.role === 'customer' ? customerNavigation : baseNavigation).map((item) => {
              const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
            
            {(currentUserRole === 'owner' || currentUserRole === 'supervisor') && (
                <>
                <div className="pt-4 pb-2 px-3">
                    <span className="text-xs font-semibold text-slate-500 uppercase">Financials</span>
                </div>
                {getFilteredNavigation(financialNavigation).map((item) => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                    <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                        ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white shadow-lg'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                    >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    {item.name}
                    </Link>
                );
                })}
                </>
            )}

            {(currentUserRole === 'owner' || currentUserRole === 'supervisor') && (
                <>
                <div className="pt-4 pb-2 px-3">
                    <span className="text-xs font-semibold text-slate-500 uppercase">Admin</span>
                </div>
                {getFilteredNavigation(adminNavigation).map((item) => {
                const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
                return (
                    <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                    >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    {item.name}
                    </Link>
                );
                })}
                </>
            )}

            <div className="pt-4 pb-2 px-3">
                <span className="text-xs font-semibold text-slate-500 uppercase">Help & Guides</span>
            </div>
            {getFilteredNavigation(helpNavigation).map((item) => {
            const isActive = location.pathname === item.href;
            return (
                <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
                onClick={() => setSidebarOpen(false)}
                >
                <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                {item.name}
                </Link>
            );
            })}
          </nav>

          <div className="mt-auto p-4 border-t border-slate-700 space-y-1">
            {getFilteredNavigation(settingsNavigation).map((item) => {
                const isActive = location.pathname === item.href;
                return (
                    <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                    >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    {item.name}
                    </Link>
                );
            })}
             {user && (
              <Button 
                variant="ghost" 
                className="w-full justify-start text-slate-300 hover:bg-red-800 hover:text-white"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-slate-900/95 backdrop-blur-xl border-b border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-slate-300 hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <TenantSwitcher />
               <Link to="/assets/create-qr">
                <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" aria-label="Create QR Code">
                  <PlusSquare className="w-5 h-5" />
                </Button>
              </Link>
               <Link to="/assets/scan">
                <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" aria-label="Scan QR Code">
                  <QrCode className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/notifications">
                <Button variant="ghost" size="icon" className="relative text-slate-300 hover:text-white">
                  <Bell className="w-5 h-5" />
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-slate-900"></span>
                </Button>
              </Link>
              {user ? (
                <div className="flex items-center space-x-2">
                   <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex items-center space-x-2 p-1 rounded-full">
                        <img src={userAvatar} alt="User Avatar" className="w-8 h-8 rounded-full object-cover border-2 border-purple-500" />
                        <div className="text-right hidden sm:block">
                          <p className="text-sm font-medium text-white">{currentUserName}</p>
                          <p className="text-xs text-slate-400 capitalize">{currentUserRole}</p>
                        </div>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 bg-slate-800 border-slate-700 text-white">
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium leading-none">{currentUserName}</p>
                          <p className="text-xs leading-none text-slate-400">{user.email}</p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-slate-700" />
                      <DropdownMenuItem onSelect={() => navigate('/settings')} className="hover:bg-slate-700 cursor-pointer">
                        <SettingsIcon className="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-slate-700"/>
                      <DropdownMenuItem onSelect={handleLogout} className="hover:bg-red-800/80 cursor-pointer focus:bg-red-800/90">
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Logout</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-300 hover:text-white">
                    <LogIn className="w-5 h-5 mr-2" />
                    Login
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          <div className="mx-auto w-full max-w-7xl">
            {children}
          </div>
        </main>
      </div>
      <OnboardingTour />
    </div>
  );
};

export default Layout;